import { useState } from "react";
import { Modal, Row, Col, Form } from "react-bootstrap";
import { MdChevronLeft, MdChevronRight, MdCalendarToday } from "react-icons/md";
import "./BookAppointmentModal.css";

/* ── helpers ── */
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const TIME_SLOTS = [
  "08:30 AM - 09:00 AM",
  "09:00 AM - 09:30 AM",
  "09:30 AM - 10:00 AM",
  "10:00 AM - 10:30 AM",
  "10:30 AM - 11:00 AM",
  "11:00 AM - 11:30 AM",
  "11:30 AM - 12:00 PM",
  "12:00 PM - 12:30 PM",
  "12:30 PM - 01:00 PM",
  "01:00 PM - 01:30 PM",
  "01:30 PM - 02:00 PM",
  "02:00 PM - 02:30 PM",
];

/* slot status: "available" | "selected" | "unavailable" */
const SLOT_STATUS = [
  "available","selected","available","selected",
  "selected","available","selected","available",
  "available","selected","available","selected",
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

/**
 * getDayStatus — returns the visual status for a calendar day.
 * "none"      → grey  (past / no data yet)
 * "available" → green (slots open)
 * "leave"     → red   (doctor on leave / fully booked)
 *
 * Replace this function body with real API data when ready.
 * Signature: (day: number, month: number, year: number) => "none" | "available" | "leave"
 */
function getDayStatus(day, month, year) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(year, month, day);
  if (date < today) return "none";
  // demo pattern — odd days available, even days leave
  return day % 2 === 0 ? "leave" : "available";
}

/* ── Mini Calendar ── */
const MiniCalendar = ({ selectedDate, onSelect }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(
    selectedDate ? selectedDate.getFullYear() : today.getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    selectedDate ? selectedDate.getMonth() : today.getMonth()
  );

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSelected = (d) =>
    selectedDate &&
    selectedDate.getDate() === d &&
    selectedDate.getMonth() === viewMonth &&
    selectedDate.getFullYear() === viewYear;

  const isToday = (d) =>
    today.getDate() === d &&
    today.getMonth() === viewMonth &&
    today.getFullYear() === viewYear;

  return (
    <div className="bam-calendar">
      <div className="bam-calendar__nav">
        <button className="bam-calendar__nav-btn" onClick={prevMonth} type="button">
          <MdChevronLeft size={18} />
        </button>
        <span className="bam-calendar__month-label">
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button className="bam-calendar__nav-btn bam-calendar__nav-btn--dark" onClick={nextMonth} type="button">
          <MdChevronRight size={18} />
        </button>
      </div>

      <div className="bam-calendar__grid">
        {DAYS.map(d => (
          <div key={d} className="bam-calendar__day-name">{d}</div>
        ))}
        {cells.map((day, idx) => {
          if (day === null) return <div key={`e-${idx}`} />;
          const status = getDayStatus(day, viewMonth, viewYear);
          const selected = isSelected(day);
          return (
            <button
              key={day}
              type="button"
              disabled={status === "none" || status === "leave"}
              onClick={() => status === "available" && onSelect(new Date(viewYear, viewMonth, day))}
              className={[
                "bam-calendar__day",
                selected
                  ? "bam-calendar__day--selected"
                  : isToday(day)
                  ? "bam-calendar__day--today"
                  : status === "available"
                  ? "bam-calendar__day--available"
                  : status === "leave"
                  ? "bam-calendar__day--leave"
                  : "bam-calendar__day--none",
              ].join(" ")}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ── Main Modal ── */
const BookAppointmentModal = ({ show, onHide, fees = "$2000" }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const dateAndSlotChosen = selectedDate !== null && selectedSlot !== null;

  const [form, setForm] = useState({
    fullName: "", mobile: "", email: "", dob: "",
  });

  const handleFormChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Appointment:", { selectedDate, selectedSlot, ...form, fees });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="bam-modal"
      contentClassName="bam-modal__content"
    >
      <Modal.Body className="bam-modal__body">
        {/* ── Title ── */}
        <h4 className="bam-modal__title">Choose Date and Time</h4>

        {/* ── Legend ── */}
        <div className="bam-legend">
          <span className="bam-legend__item">
            <span className="bam-legend__dot bam-legend__dot--selected" /> Selected
          </span>
          <span className="bam-legend__item">
            <span className="bam-legend__dot bam-legend__dot--available" /> Available
          </span>
          <span className="bam-legend__item">
            <span className="bam-legend__dot bam-legend__dot--leave" /> Doctor Leave
          </span>
        </div>

        {/* ── Top section: calendar + time slots ── */}
        <Row className="bam-top g-3">
          <Col xs={12} md={5} lg={4}>
            <MiniCalendar selectedDate={selectedDate} onSelect={setSelectedDate} />
          </Col>

          <Col xs={12} md={7} lg={8}>
            <div className="bam-slots">
              {TIME_SLOTS.map((slot, idx) => {
                const status = SLOT_STATUS[idx % SLOT_STATUS.length];
                const isChosen = selectedSlot === idx;
                return (
                  <button
                    key={idx}
                    type="button"
                    disabled={status === "unavailable"}
                    onClick={() => status !== "unavailable" && setSelectedSlot(idx)}
                    className={[
                      "bam-slot",
                      isChosen ? "bam-slot--chosen" :
                        status === "selected" ? "bam-slot--selected" :
                        status === "unavailable" ? "bam-slot--unavailable" :
                        "bam-slot--available",
                    ].join(" ")}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>
          </Col>
        </Row>

        {/* ── Appointment Form (shown only after date + slot chosen) ── */}
        {dateAndSlotChosen && (
          <div className="bam-form-section">
            <h5 className="bam-form-title">Appointment Form</h5>
            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="bam-form-label">Full Name</Form.Label>
                    <Form.Control
                      className="bam-form-input"
                      name="fullName"
                      placeholder="Full Name"
                      value={form.fullName}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="bam-form-label">Mobile Number</Form.Label>
                    <Form.Control
                      className="bam-form-input"
                      name="mobile"
                      placeholder="Mobile No."
                      value={form.mobile}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="bam-form-label">Email ID</Form.Label>
                    <Form.Control
                      className="bam-form-input"
                      name="email"
                      placeholder="Email ID"
                      value={form.email}
                      onChange={handleFormChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="bam-form-label">Date Of Birth</Form.Label>
                    <div className="bam-form-input-icon-wrap">
                      <Form.Control
                        className="bam-form-input"
                        name="dob"
                        placeholder="Date Of Birth"
                        type="date"
                        value={form.dob}
                        onChange={handleFormChange}
                      />
                      <MdCalendarToday className="bam-form-input-icon" size={16} />
                    </div>
                  </Form.Group>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Group>
                    <Form.Label className="bam-form-label">Fees</Form.Label>
                    <Form.Control
                      className="bam-form-input"
                      readOnly
                      value={fees}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="bam-form-footer">
                <button type="submit" className="bam-pay-btn">
                  Make Payment
                </button>
              </div>
            </Form>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BookAppointmentModal;
